import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Clock, Play, Loader2, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import RunHistoryPopover from "./RunHistoryPopover";
import FailureDetails from "./FailureDetails";
import NextRunIndicator from "./NextRunIndicator";
import type { TrackedJob, CronRow, RunLogRow, FailureInfo } from "./types";

interface Props {
  job: TrackedJob;
  cron?: CronRow;
  log?: RunLogRow;
  isRunning: boolean;
  anyRunning: boolean;
  failure?: FailureInfo;
  detailsOpen: boolean;
  onTrigger: (job: TrackedJob) => void;
  onDetailsOpenChange: (open: boolean) => void;
}

const statusPill = (cron: CronRow | undefined, log: RunLogRow | undefined) => {
  const status = log ? (log.success ? "succeeded" : "failed") : cron?.status ?? null;
  if (!status) return (
    <Badge variant="outline" className="gap-1 text-[10px] h-5">
      <Clock size={10} /> No recent run
    </Badge>
  );
  if (status === "succeeded") return (
    <Badge variant="outline" className="gap-1 text-[10px] h-5 border-primary/40 text-primary bg-primary/10">
      <CheckCircle2 size={10} /> OK
    </Badge>
  );
  if (status === "failed") return (
    <Badge variant="destructive" className="gap-1 text-[10px] h-5">
      <XCircle size={10} /> Failed
    </Badge>
  );
  return <Badge variant="secondary" className="text-[10px] h-5">{status}</Badge>;
};

const MaintenanceRow = ({
  job, cron, log, isRunning, anyRunning, failure, detailsOpen, onTrigger, onDetailsOpenChange,
}: Props) => {
  const ranAt = log?.ran_at ?? cron?.start_time ?? null;
  const failedFromLog = log && !log.success;
  const hasFailureInfo = !!failure || failedFromLog;

  return (
    <div className="space-y-1">
      <div className="flex items-stretch border border-border rounded-md overflow-hidden group">
        <button
          type="button"
          onClick={() => onTrigger(job)}
          disabled={isRunning || anyRunning}
          className="flex-1 flex items-center justify-between px-3 py-2 text-left hover:bg-muted/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-w-0"
          title={job.destructive ? `Run ${job.functionName} (destructive — confirm required)` : `Run ${job.functionName} now`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {isRunning ? (
              <Loader2 size={13} className="animate-spin text-muted-foreground shrink-0" />
            ) : job.destructive ? (
              <AlertTriangle size={13} className="text-destructive shrink-0 opacity-80 group-hover:opacity-100" />
            ) : (
              <Play size={13} className="text-muted-foreground shrink-0 opacity-60 group-hover:opacity-100" />
            )}
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium truncate">{job.label}</span>
              <span className="text-[10px] text-muted-foreground truncate" title={job.functionName}>
                {log
                  ? `${log.affected_count} ${job.countLabel}`
                  : cron?.start_time
                    ? "No count logged yet"
                    : job.functionName}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
            {statusPill(cron, log)}
            <span className="text-[10px] text-muted-foreground" title={ranAt ?? ""}>
              {ranAt ? `${formatDistanceToNow(new Date(ranAt))} ago` : "—"}
            </span>
            <NextRunIndicator schedule={cron?.schedule} active={cron?.active} />
          </div>
        </button>
        <RunHistoryPopover job={job} />
      </div>
      {hasFailureInfo && (
        <FailureDetails
          open={detailsOpen}
          onOpenChange={onDetailsOpenChange}
          failure={failure}
          log={log}
        />
      )}
    </div>
  );
};

export default MaintenanceRow;
